import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import { Button } from 'antd';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TbChevronDown, TbFilter, TbPlus } from 'react-icons/tb';
import { IconWrapper } from '../../../icon/IconWrapper.tsx';
import { type IPopoverHandle, Popover } from '../../../popover/Popover.tsx';
import { type SearchableField } from '../../ISearchableField.tsx';
import { FilterBarAddPopoverContent } from './FilterBarAddPopoverContent.tsx';

export interface IFilterBarAddPopoverProps {
  variant: 'toolbar' | 'inline';
  fields: SearchableField[];
  /**
   * Query corrente (parsata): propagata all'editor del campo scelto come
   * sorgente di verità per i valori iniziali. Per un campo "nuovo" tipicamente
   * non c'è una chiave attiva, ma propagarla mantiene il comportamento corretto
   * se il chip fosse stato pre-popolato altrove.
   */
  query: Record<string, any>;
  /** Schema dei filtri, propagato al `FormHandler` annidato del popover. */
  schema: YupAnyObjectSchema;
  /** Nomi dei campi già visualizzati come chip (esclusi dalla lista). */
  excludeNames: string[];
  /** Chiamata su Applica quando l'utente conferma il nuovo filtro. */
  onAdded: (name: string) => void;
}

/**
 * Popover a 2 step per aggiungere un filtro (pick → edit), in due varianti
 * grafiche: `toolbar` (bottone `[⏷ Filtri di Ricerca]`) e `inline` (chip
 * dashed `[+ Filtro]`). `onAdded(name)` scatta solo su "Applica" dello step
 * edit — così "Annulla" lascia lo stato invariato.
 */
export function FilterBarAddPopover({
  variant,
  fields,
  query,
  schema,
  excludeNames,
  onAdded,
}: Readonly<IFilterBarAddPopoverProps>) {
  const { t } = useTranslation();
  const popoverRef = useRef<IPopoverHandle>(null);

  const trigger =
    variant === 'toolbar' ? (
      <Button
        type="text"
        size="small"
        icon={<IconWrapper icon={<TbFilter />} />}
      >
        {t('filter-bar.add-filter-button', 'Filtri di Ricerca')}
        <IconWrapper icon={<TbChevronDown />} />
      </Button>
    ) : (
      <Button
        size="middle"
        type="dashed"
        icon={<IconWrapper icon={<TbPlus />} />}
      >
        {t('filter-bar.add-filter-chip', 'Filtro')}
      </Button>
    );

  return (
    <Popover
      ref={popoverRef}
      content={
        <FilterBarAddPopoverContent
          fields={fields}
          query={query}
          schema={schema}
          excludeNames={excludeNames}
          onAdded={onAdded}
          onClose={() => popoverRef.current?.close()}
        />
      }
      placement="bottomLeft"
      arrow={false}
      autoFocusField
      allowPortalClicks
    >
      {trigger}
    </Popover>
  );
}
